import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { Skeleton } from '@/components/feedback/skeleton'
import { FormField } from '@/components/forms/form-field'
import { Select, type SelectOption } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/primitives/button'
import { AgentToolBindingCheckboxes } from '@/features/admin/agent-tools/tool-binding-checkboxes'
import { listRegisteredModels, type RegisteredModel } from '@/features/admin/model-provider/api'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { useHubAgentName } from '@/features/shared/brand/branding-hooks'
import { coreAgentStatusQueryKey } from '@/features/user/core-agent/api'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'

import {
  getCoreAgentSettings,
  updateCoreAgentSettings,
  type CoreAgentSettings,
  type CoreAgentSettingsPayload,
} from './api'
import { ModelSelectionDrawer, type CoreAgentModelOption } from './model-selection-drawer'

type CoreAgentErrors = Partial<Record<'allowedModels' | 'defaultModel', string>>

function registeredModelDisplayName(model: RegisteredModel) {
  const publicName = model.public_model_name.trim()
  const modelName = model.model_name.trim()

  return publicName ? publicName : modelName ? modelName : model.id
}

function registeredModelProviderLabel(model: RegisteredModel) {
  const providerLabel = model.provider_label?.trim()
  const provider = model.provider.trim()

  if (providerLabel) {
    return providerLabel
  }

  return provider ? provider.toUpperCase() : ''
}

function modelProviderIcon(model: CoreAgentModelOption) {
  return (
    <ProviderLogo
      className="size-5 rounded-md p-1"
      label={model.providerLabel}
      provider={model.provider}
      variant="compact"
    />
  )
}

function settingsFormKey(settings: CoreAgentSettings) {
  return [
    settings.enabled ? 'enabled' : 'disabled',
    settings.default_registered_model_id,
    settings.allowed_registered_model_ids.join(','),
    settings.updated_at ?? '',
  ].join(':')
}

export function AdminCoreAgentSettingsPage() {
  const { t } = useTranslation('adminSettings')
  const hubAgentName = useHubAgentName()
  const role = readStoredPortalAccount('admin').role
  const canWrite = role === 'admin' || role === 'super_admin'
  const settingsQuery = useQuery({
    queryFn: getCoreAgentSettings,
    queryKey: ['admin-core-agent-settings'],
  })
  const registeredModelsQuery = useQuery({
    queryFn: () => listRegisteredModels({ page: 1, pageSize: 200, status: 'active' }),
    queryKey: ['admin-core-agent-registered-models'],
    staleTime: 60_000,
  })
  const modelOptions = useMemo(
    () =>
      (registeredModelsQuery.data?.items ?? []).map((model) => ({
        id: model.id,
        label: registeredModelDisplayName(model),
        provider: model.provider,
        providerLabel: registeredModelProviderLabel(model),
      })),
    [registeredModelsQuery.data?.items],
  )

  return (
    <AdminShellPage activePath="/admin/agent/hub">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        {settingsQuery.isError ? (
          <>
            <PageHeader
              contentClassName="max-w-[760px]"
              description={t('coreAgent.description', { hubAgentName })}
              title={t('coreAgent.title', { hubAgentName })}
            />
            <div className="flex flex-1 items-center justify-center px-6">
              <ErrorState
                description={t('coreAgent.loadDescription', { hubAgentName })}
                onRetry={() => {
                  void settingsQuery.refetch()
                }}
                title={t('coreAgent.loadTitle', { hubAgentName })}
              />
            </div>
          </>
        ) : settingsQuery.isLoading || !settingsQuery.data ? (
          <>
            <PageHeader
              contentClassName="max-w-[760px]"
              description={t('coreAgent.description', { hubAgentName })}
              title={t('coreAgent.title', { hubAgentName })}
            />
            <div className="px-5 py-5 lg:px-[30px]">
              <PageContentBoundary className="grid max-w-[760px] gap-5">
                <Skeleton className="h-24 rounded-xl" />
                <Skeleton className="h-44 rounded-xl" />
                <Skeleton className="h-36 rounded-xl" />
              </PageContentBoundary>
            </div>
          </>
        ) : (
          <CoreAgentSettingsForm
            canWrite={canWrite}
            key={settingsFormKey(settingsQuery.data)}
            loadingModels={registeredModelsQuery.isLoading}
            modelOptions={modelOptions}
            settings={settingsQuery.data}
            hubAgentName={hubAgentName}
          />
        )}
      </div>
    </AdminShellPage>
  )
}

function CoreAgentSettingsForm({
  canWrite,
  hubAgentName,
  loadingModels,
  modelOptions,
  settings,
}: {
  canWrite: boolean
  hubAgentName: string
  loadingModels: boolean
  modelOptions: CoreAgentModelOption[]
  settings: CoreAgentSettings
}) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(settings.enabled)
  const [showToolInvocationDetails, setShowToolInvocationDetails] = useState(
    settings.show_tool_invocation_details,
  )
  const [systemPrompt, setSystemPrompt] = useState(settings.system_prompt)
  const [allowedModelIds, setAllowedModelIds] = useState(settings.allowed_registered_model_ids)
  const [defaultModelId, setDefaultModelId] = useState(settings.default_registered_model_id)
  const [modelDrawerOpen, setModelDrawerOpen] = useState(false)
  const [errors, setErrors] = useState<CoreAgentErrors>({})
  const allowedModelIdSet = useMemo(() => new Set(allowedModelIds), [allowedModelIds])
  const defaultModelOptions = useMemo<SelectOption[]>(
    () => [
      { label: t('coreAgent.defaultModelPlaceholder'), value: '' },
      ...modelOptions
        .filter((model) => allowedModelIdSet.has(model.id))
        .map((model) => ({
          icon: modelProviderIcon(model),
          label: model.label,
          value: model.id,
        })),
    ],
    [allowedModelIdSet, modelOptions, t],
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      const nextErrors: CoreAgentErrors = {}
      if (enabled && allowedModelIds.length === 0) {
        nextErrors.allowedModels = t('coreAgent.validation.allowedModelsRequired')
      }
      if (enabled && !defaultModelId) {
        nextErrors.defaultModel = t('coreAgent.validation.defaultModelRequired')
      }
      if (defaultModelId && !allowedModelIdSet.has(defaultModelId)) {
        nextErrors.defaultModel = t('coreAgent.validation.defaultModelMustBeAllowed')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return undefined
      }

      setErrors({})
      const payload: CoreAgentSettingsPayload = {
        allowed_registered_model_ids: allowedModelIds,
        default_registered_model_id: defaultModelId,
        enabled,
        show_tool_invocation_details: showToolInvocationDetails,
        system_prompt: systemPrompt,
      }
      return updateCoreAgentSettings(payload)
    },
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result) => {
      if (!result) {
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-core-agent-settings'] })
      queryClient.setQueryData(coreAgentStatusQueryKey, { enabled: result.enabled })
      await queryClient.invalidateQueries({ queryKey: ['core-agent-view-config'] })
      showFeedback({
        message: t('success.updateCoreAgentSettings', { hubAgentName }),
        tone: 'success',
      })
    },
  })

  const selectedModels = useMemo(
    () => modelOptions.filter((model) => allowedModelIdSet.has(model.id)),
    [allowedModelIdSet, modelOptions],
  )

  return (
    <>
      <PageHeader
        contentClassName="max-w-[760px]"
        description={t('coreAgent.description', { hubAgentName })}
        title={t('coreAgent.title', { hubAgentName })}
      />
      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-5 lg:px-[30px]">
        <PageContentBoundary className="grid max-w-[760px] gap-4">
          {!canWrite ? (
            <InlineNotice tone="info">{t('coreAgent.viewerNotice')}</InlineNotice>
          ) : null}
          <div className="overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white shadow-[var(--shadow-panel-subtle)]">
            <section className="px-5 py-5 sm:px-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-[15px] font-semibold text-[var(--color-text)]">
                    {t('coreAgent.enableTitle', { hubAgentName })}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    {t('coreAgent.enableDescription', { hubAgentName })}
                  </p>
                </div>
                <Switch
                  aria-label={t('coreAgent.enableTitle', { hubAgentName })}
                  checked={enabled}
                  disabled={!canWrite}
                  onCheckedChange={setEnabled}
                />
              </div>
            </section>

            <section className="grid gap-4 border-t border-[var(--color-divider)] px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-[15px] font-semibold text-[var(--color-text)]">
                    {t('coreAgent.modelsTitle', { hubAgentName })}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    {t('coreAgent.modelsDescription', { hubAgentName })}
                  </p>
                </div>
                {canWrite && modelOptions.length > 0 ? (
                  <Button
                    className="shrink-0"
                    onClick={() => setModelDrawerOpen(true)}
                    size="sm"
                    variant="secondary"
                  >
                    {t('coreAgent.modelPicker.open')}
                  </Button>
                ) : null}
              </div>
              <FormField
                error={errors.allowedModels}
                label={t('coreAgent.allowedModels')}
                required={enabled}
              >
                <div className="overflow-hidden rounded-lg border border-[var(--color-panel-border)]">
                  {loadingModels ? (
                    <div className="grid gap-2 p-2">
                      <Skeleton className="h-9 rounded-lg" />
                      <Skeleton className="h-9 rounded-lg" />
                    </div>
                  ) : modelOptions.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-[var(--color-text-muted)]">
                      {t('coreAgent.noModels')}
                    </div>
                  ) : selectedModels.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-[var(--color-text-muted)]">
                      {t('coreAgent.modelPicker.noSelection')}
                    </div>
                  ) : (
                    <div className="divide-y divide-[var(--color-divider)]">
                      {selectedModels.map((model) => (
                        <div
                          className="flex min-h-10 items-center gap-2 px-3 text-sm text-[var(--color-text)]"
                          key={model.id}
                        >
                          {modelProviderIcon(model)}
                          <span className="min-w-0 truncate font-medium">{model.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FormField>
              <FormField
                error={errors.defaultModel}
                htmlFor="core-agent-default-model"
                label={t('coreAgent.defaultModel')}
                required={enabled}
              >
                <Select
                  aria-label={t('coreAgent.defaultModel')}
                  disabled={!canWrite || allowedModelIds.length === 0}
                  onValueChange={(value) => {
                    setDefaultModelId(value)
                    setErrors((existing) => ({ ...existing, defaultModel: undefined }))
                  }}
                  options={defaultModelOptions}
                  value={defaultModelId}
                />
              </FormField>
            </section>

            <section className="border-t border-[var(--color-divider)] px-5 py-5 sm:px-6">
              <FormField
                hint={t('coreAgent.systemPromptHint', { hubAgentName })}
                htmlFor="core-agent-system-prompt"
                label={t('coreAgent.systemPrompt')}
              >
                <Textarea
                  className="min-h-[140px] resize-y rounded-lg border-[var(--color-control-border)] bg-[var(--color-surface-muted)] text-sm"
                  disabled={!canWrite}
                  id="core-agent-system-prompt"
                  onChange={(event) => {
                    setSystemPrompt(event.target.value)
                  }}
                  placeholder={t('coreAgent.systemPromptPlaceholder')}
                  value={systemPrompt}
                />
              </FormField>
            </section>

            <section className="grid gap-5 border-t border-[var(--color-divider)] px-5 py-5 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-[15px] font-semibold text-[var(--color-text)]">
                    {t('coreAgent.showToolDetails')}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    {t('coreAgent.showToolDetailsHint')}
                  </p>
                </div>
                <Switch
                  aria-label={t('coreAgent.showToolDetails')}
                  checked={showToolInvocationDetails}
                  disabled={!canWrite}
                  onCheckedChange={setShowToolInvocationDetails}
                />
              </div>
              <div className="border-t border-[var(--color-divider)] pt-5">
                <AgentToolBindingCheckboxes owner={{ agentId: 'core', agentType: 'core_agent' }} />
              </div>
            </section>
            {canWrite ? (
              <div className="flex justify-end border-t border-[var(--color-divider)] px-5 py-4 sm:px-6">
                <Button isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                  {t('actions.save')}
                </Button>
              </div>
            ) : null}
          </div>
        </PageContentBoundary>
      </div>
      {modelDrawerOpen ? (
        <ModelSelectionDrawer
          models={modelOptions}
          onApply={(modelIds) => {
            setAllowedModelIds(modelIds)
            if (!modelIds.includes(defaultModelId)) {
              setDefaultModelId(modelIds[0] ?? '')
            }
            setErrors((existing) => ({
              ...existing,
              allowedModels: undefined,
              defaultModel: undefined,
            }))
          }}
          onOpenChange={setModelDrawerOpen}
          selectedIds={allowedModelIds}
        />
      ) : null}
    </>
  )
}
